// Session BBF. The office client's types for an insurer's findings (server/src/modules/insurance). The SPA cannot import server code,
// so the DTO is mirrored here; the state and its label come from the server on every read (derived live, never stored), so nothing
// about the rules is duplicated in the browser.

export type InsuranceOutcome = "verified_present" | "not_present" | "needs_parts" | "needs_return_visit" | "unable_to_check";
export type InsuranceItemState =
  | "archived"
  | "awaiting_job"
  | "check_booked"
  | "awaiting_answer"
  | "closed_not_present"
  | "unable_to_check"
  | "verified"
  | "defect_raised"
  | "needs_estimate"
  | "estimate_linked";

export interface InsuranceItemDto {
  id: string;
  assetId: string;
  lift: { eleNumber: string | null; liftType: string | null; retired: boolean };
  site: { id: string; name: string } | null;
  customer: { id: string; name: string } | null;
  insurer: string;
  reportReference: string | null;
  reportDate: string | null; // YYYY-MM-DD
  examinationDate: string | null; // YYYY-MM-DD
  wording: string; // the insurer's own words, verbatim
  insurerCategory: string | null;
  source: { messageId: string | null; driveFileId: string | null; filename: string | null };
  state: InsuranceItemState;
  stateLabel: string;
  job: { id: string; status: string; scheduledFor: string | null; engineerName: string | null } | null;
  outcome: InsuranceOutcome | null;
  outcomeLabel: string | null;
  outcomeNote: string | null; // the engineer's own words
  checkedAt: string | null;
  photoCount: number;
  proposedSeverity: "advisory" | "immediate" | null;
  defect: { id: string; status: string; severity: string } | null;
  repairEstimate: { id: string; reference: string; status: string } | null;
  offers: { raiseDefect: boolean; raiseEstimate: boolean; bookCheck: boolean };
  archived: boolean;
  createdAt: string;
}

export interface InsuranceItemListResult {
  items: InsuranceItemDto[];
  total: number;
  limit: number;
  offset: number;
}

export interface InsuranceItemInput {
  wording: string;
  insurerCategory?: string | null;
}

export interface RecordInsuranceItemsBody {
  assetId: string;
  insurer: string;
  reportReference?: string | null;
  reportDate?: string | null;
  examinationDate?: string | null;
  sourceFilename?: string | null;
  sourceMessageId?: string | null;
  items: InsuranceItemInput[];
  preview?: boolean;
}

export interface RecordInsuranceItemsResult {
  preview: boolean;
  created: InsuranceItemDto[];
  wouldCreate: { wording: string; insurerCategory: string | null }[];
  skipped: { wording: string; reason: string }[];
}

export interface UpdateInsuranceItemBody {
  insurer?: string;
  reportReference?: string | null;
  reportDate?: string | null;
  examinationDate?: string | null;
  wording?: string;
  insurerCategory?: string | null;
  sourceFilename?: string | null;
}

export interface CheckJobBody {
  assetId: string;
  scheduledDay?: string | null;
  engineerId?: string | null;
  durationMins?: number | null;
  standalone?: boolean;
  preview?: boolean;
}

export interface CheckJobPlan {
  lift: { eleNumber: string | null; siteName: string | null };
  items: { id: string; wording: string; insurer: string }[];
  action:
    | { kind: "create"; scheduledFor: string | null; basis: "after_service" | "chosen_day" | "unscheduled"; serviceJobId: string | null; engineerId: string | null; engineerName: string | null; durationMins: number }
    | { kind: "join"; jobId: string; scheduledFor: string | null; engineerName: string | null }
    | { kind: "wait"; jobId: string; jobStatus: string };
  notes: string[];
}

export interface CheckJobResult {
  preview: boolean;
  plan?: CheckJobPlan;
  jobId?: string;
  joined?: boolean;
  attached?: number;
  scheduledFor?: string | null;
  engineerName?: string | null;
  notes?: string[];
}

export interface RaiseDefectPlan {
  preview: boolean;
  severity?: "advisory" | "immediate";
  description?: string;
  defectId?: string;
  item?: InsuranceItemDto;
}

export const STATE_TONE: Record<InsuranceItemState, string> = {
  archived: "text-ele-body opacity-70",
  awaiting_job: "text-amber-400",
  check_booked: "text-ele-blue",
  awaiting_answer: "text-ele-blue",
  closed_not_present: "text-ele-green",
  unable_to_check: "text-amber-400",
  verified: "text-amber-400",
  defect_raised: "text-ele-green",
  needs_estimate: "text-amber-400",
  estimate_linked: "text-ele-green",
};
